import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, effect, ElementRef, inject, input, output, ViewChild } from '@angular/core';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/material.module';

@Component({
  selector: 'app-filters',
  standalone: true,
  imports: [MaterialModule, CommonModule, FormsModule],
  templateUrl: './filters.component.html',
  styleUrl: './filters.component.scss'
})
export class FiltersComponent implements AfterViewInit {
  filterSelected = output<any>();
  typeSelected = output<any>();
  filterTimeline = input<string>()
  options = ['Todo', 'Overdue', 'Today', 'Tomorrow', 'This week', 'Next week', 'All'];
  selectedOption = 'Todo';
  typeOption = 'All'
  showDatePicker: boolean = false;
  startDate: Date | null = null;
  endDate: Date | null = null;
  @ViewChild('picker') picker!: MatDateRangePicker<Date>;
  types = input<any>();
  selectedTypes: string[] = [];
  hideTimeLineFilter= input<boolean>(false)
  defaultSelectedTypeIds = input<number[]>([])
  useMultiselect = input<boolean>(false) // New input to control multiselect mode

  // References to DOM nodes for overlap detection
  @ViewChild('firstGroup', { static: true }) firstGroup!: ElementRef<HTMLDivElement>;
  @ViewChild('secondGroup', { static: false }) secondGroup?: ElementRef<HTMLDivElement>;
  @ViewChild('secondGroupMultiselect', { static: false }) secondGroupMultiselect?: ElementRef<HTMLDivElement>;
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;

  overlap = false; // Flag for template to use
  private ro!: ResizeObserver; // Handle for ResizeObserver
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  showIcons: boolean = false;

  constructor() {
    effect(() => {
      if (this.filterTimeline()) {
        const timeLineFilter = localStorage.getItem(this.filterTimeline()) || 'Todo';
        this.selectedOption = timeLineFilter;
      }

      // Only check overlap if not using multiselect
      if(this.types()?.length > 5 && !this.useMultiselect()){
        this.showIcons = true;
        this.checkOverlap();
      }

      // Initialize selected types based on defaultSelectedTypeIds
      if (this.defaultSelectedTypeIds()?.length > 0 && this.types()?.length > 0) {
        this.selectedTypes = this.types().filter((type: any) =>
          this.defaultSelectedTypeIds().includes(type.id)
        );
      }
    });
  }

  ngOnInit(){

  }

  ngAfterViewInit() {
    // Only set up overlap detection if not using multiselect
    if (!this.useMultiselect()) {
      // Run overlap check once immediately
      this.checkOverlap();

      // Re-run whenever the container is resized
      this.ro = new ResizeObserver(() => this.checkOverlap());
      this.ro.observe(this.container.nativeElement);

      // Clean up when component is destroyed
      this.destroyRef.onDestroy(() => {
        if (this.ro) {
          this.ro.disconnect();
        }
      });
    }
  }

  /** Determines whether firstGroup and secondGroup overlap horizontally */
  private checkOverlap() {
    // Skip overlap check if using multiselect or if elements don't exist
    if (this.useMultiselect() || !this.secondGroup?.nativeElement) {
      return;
    }

    const containerRect = this.container.nativeElement.getBoundingClientRect();
    const firstRect = this.firstGroup.nativeElement.getBoundingClientRect();
    const secondRect = this.secondGroup.nativeElement.getBoundingClientRect();

    // Calculate available space
    const containerWidth = containerRect.width;
    const firstGroupWidth = firstRect.width;
    const secondGroupWidth = secondRect.width;
    const minGap = 8; // Minimum space between groups

    // Check if there's enough space
    const totalNeededWidth = firstGroupWidth + secondGroupWidth + minGap;
    const newValue = totalNeededWidth > containerWidth;

    if(this.showIcons){
      this.overlap = true;
      this.cdr.markForCheck();
    }else if (this.overlap !== newValue) {
      this.overlap =  newValue ;
      this.cdr.markForCheck(); // Tell Angular a bound value changed
    }
  }

  selectOption(option: string) {
    this.selectedOption = option;
    localStorage.setItem(this.filterTimeline(), this.selectedOption);
    this.filterSelected.emit(this.selectedOption);
  }

  selectTypeOption(option: string) {
    this.typeOption = option;
  }

  // Check if a type is selected
  isSelected(type: any): boolean {
    return this.selectedTypes.includes(type);
  }

  // Toggle selection
  toggleSelection(type: string) {
    if (type === 'All') {
      // If "All" is clicked, select or deselect everything
      if (this.selectedTypes.length >= this.types().length) {
        this.selectedTypes = []; // Deselect all
      } else {
        this.selectedTypes = [...this.types()]; // Select all
      }
      this.typeSelected.emit(this.selectedTypes.map((row: any) => row.id));
    } else {
      // Toggle individual types
      const index = this.selectedTypes.indexOf(type);
      if (index > -1) {
        this.selectedTypes.splice(index, 1); // Deselect type
      } else {
        this.selectedTypes.push(type); // Select type
      }

      this.typeSelected.emit(this.selectedTypes.map((row: any) => row.id));
    }
  }

  // Update "All" selection dynamically
  updateAllSelection() {
    // Implementation commented out in original code
  }

  // Handle multiselect dropdown changes
  onMultiselectChange(selectedTypeIds: number[]) {
    if (selectedTypeIds && selectedTypeIds.length > 0) {
      this.selectedTypes = this.types().filter((type: any) =>
        selectedTypeIds.includes(type.id)
      );
    } else {
      this.selectedTypes = [];
    }
    this.typeSelected.emit(selectedTypeIds);
  }

  // Get selected type IDs for the multiselect
  getSelectedTypeIds(): number[] {
    return this.selectedTypes.map((type: any) => type.id);
  }
}